<template>
  <div id="MainSystemSearch" :dir="$vs.rtl ? 'rtl' : 'ltr'">
    <form class="care-search" @submit.prevent="doSearch">
      <div class="search-heading"><div><h2>{{ copy.title }}</h2><p>{{ copy.subtitle }}</p></div><feather-icon icon="SearchIcon" /></div>
      <div class="search-fields">
        <div class="search-field" v-for="field in filterFields" :key="field.key">
          <label :for="'care-' + field.key"><feather-icon :icon="field.icon" />{{ field.label }}</label>
          <v-select :input-id="'care-' + field.key" :dir="$vs.rtl ? 'rtl' : 'ltr'" label="Name" v-model="search[field.key]" :options="field.options || []" :reduce="item => item.ID" :placeholder="copy.any" :disabled="isLoading" />
        </div>
        <button class="care-search-button" type="submit" :disabled="isLoading"><feather-icon icon="SearchIcon" />{{ isLoading ? copy.loading : $t('Search') }}</button>
      </div>
      <div class="search-footnote"><span class="search-hint">{{ copy.hint }}</span><button v-if="hasFilters || hasSearched" type="button" class="care-clear" :disabled="isLoading" @click="clearSearch">{{ $t('Clear') }}</button></div>
    </form>
    <div v-if="isLoading" class="search-state" role="status" aria-live="polite"><span class="care-spinner" />{{ copy.loading }}</div>
    <div v-if="searchError" class="search-error" role="alert">{{ copy.error }} <button type="button" class="care-clear" :disabled="isLoading" @click="doSearch">{{ copy.retry }}</button></div>
    <!-- ============================================================ -->
    <!-- ============================================================ -->
    <div v-if="hasSearched && !isLoading" class="general-search-container">

      <!-- ============================================================ -->
      <!-- 1) Medical Packages -->
      <!-- ============================================================ -->
      <section id="MasterMedicalPackage" class="mb-10" v-if="medicalPackages && medicalPackages.length > 0">
        <h3 class="section-title">{{ $t("OurPackages") }}</h3>

        <div class="care-package-grid">
          <button class="care-package" type="button" v-for="item in medicalPackages" :key="item.ID" @click="OpenPackage(item)">
            <img v-if="item.ImagePath" :src="baseURL + item.ImagePath" :alt="item.Name" loading="lazy" @error="$event.target.style.display = 'none'" />
            <div class="care-package-body"><span class="care-package-category" v-if="item.MedicalPackageCategory">{{ item.MedicalPackageCategory.Name }}</span><h4>{{ item.Name }}</h4><span class="care-package-link">{{ copy.details }} <span aria-hidden="true">&#8594;</span></span></div>
          </button>
        </div>
      </section>

      <!-- ============================================================ -->
      <!-- 2) Surgery & Treatment -->
      <!-- ============================================================ -->
      <section id="SAT" class="mb-15" v-if="surgeryHospitals && surgeryHospitals.length > 0">
        <h3 class="section-title">{{ $t("SurgeryAndTreatment") }}</h3>

        <div style="width: 100%" class="hospitalListMobil">
          <SurgeryAndTreatmentsList
            :data="surgeryHospitals"
            @getSurgeries="getSurgeries"
          />
        </div>

        <button type="button" class="care-more" :disabled="loadingSurgery" v-if="IsShowMoreSurgery" @click="getNextHospitals">{{ $t('ShowMore') }}</button>
      </section>

      <!-- ============================================================ -->
      <!-- 3) Doctors -->
      <!-- ============================================================ -->
      <section id="docSearch" class="mb-15" v-if="searchDoctors && searchDoctors.length > 0">
        <h3 class="section-title">{{ $t("Doctors") }}</h3>

        <div class="w-full mt-1">
          <doctor-list
            class="mt-5"
            v-for="item in searchDoctors"
            :key="item.ID"
            :doctor="item"
          />
        </div>

        <button type="button" class="care-more" :disabled="loadingDoctors" v-if="IsShowMoreDoctors" @click="getNextDoctors">
          {{ $t("ShowMore") }}
        </button>
      </section>

      <!-- No Data -->
      <div v-if="noResults && !searchError" class="search-state">
        <feather-icon icon="AlertCircleIcon" style="width: 40px; height: 40px; opacity: 0.4" />
        <p class="text-base mt-3">{{ $t("NoDataToshow") }}</p><p>{{ copy.empty }}</p>
      </div>

    </div>

    <!-- ================= Popups ================= -->
    <PackageDetail v-if="selectedPackage.ID" id="PackageDetail" :PackageID="selectedPackage.ID" />

    <vs-popup fullscreen title="" :active.sync="showPopup">
      <HospitalSurgeries :itemModel="ItemModel" v-if="showPopup" @closePop="showPopup = false" />
    </vs-popup>

  </div>
</template>

<script>
// ===== Store Modules =====
import moduleMedicalPackage from "@/store/medicalPackage/moduleMedicalPackage.js";
import moduleMedicalPackageCategory from "@/store/settings/MedicalPackageCategory/moduleMedicalPackageCategory.js";
import moduleCountry from "@/store/settings/country/moduleCountry.js";
import moduleSurgery from "@/store/settings/surgery/moduleSurgery.js";
import moduleSurgeryCategory from "@/store/settings/surgeryCategory/moduleSurgeryCategory.js";
import moduleDoctor from "@/store/doctor/moduleDoctor.js";
import moduleSpecialty from "@/store/settings/specialty/moduleSpecialty.js";

// ===== Components =====
import { domain } from "@/gloabelConstant.js";

import PackageDetail from "@/views/package/PackageDetail.vue";
import SurgeryAndTreatmentsList from "@/views/Patient/Surgery/SurgeryAndTreatmentsList.vue";
import HospitalSurgeries from "@/views/Patient/Surgery/HospitalSurgeriesPopup.vue";
import DoctorList from "@/views/appointment/search/DoctorList.vue";

export default {
  components: {


    PackageDetail,
    SurgeryAndTreatmentsList,
    HospitalSurgeries,
    DoctorList,
  },

  data() {
    return {
      search: {
        CountryID: "",
        MedicalPackageCategorID: "",
        SurgeryCategoryID: "",
        SpecialtyID: "",
      },

      hasSearched: false,
      baseURL: domain,
      isLoading: false,
      loadingDoctors: false,
      loadingSurgery: false,
      searchError: false,

      // 1) Packages
      selectedPackage: {},
      pkgSearch: {
        MedicalPackageCategorID: "",
        CountryID: "",
        ValidDate: null,
        IsPublished: true,
      },

      // 2) Surgery
      showPopup: false,
      IsShowMoreSurgery: true,
      ItemModel: { Surgeries: [] },
      surgSearch: {
        SurgeryCategoryID: "",
        CountryID: "",
        PageSize: 10,
        PageNumber: 1,
        SurgeryActive: true,
      },

      // 3) Doctors
      docSearchModel: {
        SpecialtyID: "",
        CountryID: "",
        IsPaging: true,
        PageNumber: 1,
        PageSize: 10,
        ForAppointment: true,
      },
      IsShowMoreDoctors: true,
    };
  },

  computed: {
    copy() {
      return this.$vs.rtl ? {
        title: 'ابحث عن الرعاية المناسبة لك', subtitle: 'استكشف الباقات والعلاجات والأطباء في مكان واحد.', any: 'الكل', hint: 'كل الفلاتر اختيارية. اختر ما يناسب رحلتك.', loading: 'جاري البحث…', error: 'تعذر تحميل بعض النتائج. حاول مرة أخرى.', retry: 'إعادة المحاولة', empty: 'جرّب تغيير الوجهة أو إزالة بعض الفلاتر.', details: 'عرض التفاصيل', surgery: 'تخصص الجراحة', doctor: 'تخصص الطبيب'
      } : {
        title: 'Find the right care for you', subtitle: 'Explore packages, treatments and doctors in one search.', any: 'Any', hint: 'All filters are optional. Choose what matters to your journey.', loading: 'Finding your care…', error: 'Some results could not be loaded. Please try again.', retry: 'Try again', empty: 'Try another destination or clear a filter to broaden your search.', details: 'View details', surgery: 'Surgery specialty', doctor: 'Doctor specialty'
      };
    },
    hasFilters() { return Object.values(this.search).some(value => value !== '' && value != null); },
    filterFields() { return [
      { key: 'CountryID', label: this.$t('Destination'), icon: 'MapPinIcon', options: this.countries },
      { key: 'MedicalPackageCategorID', label: this.$t('PackageCategory'), icon: 'GridIcon', options: this.medicalPackageCategories },
      { key: 'SurgeryCategoryID', label: this.copy.surgery, icon: 'ActivityIcon', options: this.surgeryCategories },
      { key: 'SpecialtyID', label: this.copy.doctor, icon: 'UsersIcon', options: this.specialties }
    ]; },
    windowWidth() {
      return this.$store.state.windowWidth;
    },
    countries() {
      return this.$store.state.CountryList.countries;
    },
    medicalPackageCategories() {
      return this.$store.state.MedicalPackageCategoryList.MedicalPackageCategorys;
    },
    surgeryCategories() {
      return this.$store.state.SurgeryCategoryList.surgeryCategories;
    },
    specialties() {
      return this.$store.state.SpecialtyList.specialties;
    },
    medicalPackages() {
      return this.$store.state.MedicalPackageList.medicalPackages;
    },
    surgeryHospitals() {
      return this.$store.state.SurgeryList.hospitals || [];
    },
    searchDoctors() {
      return this.$store.state.DoctorList.searchDoctors;
    },
    noResults() {
      return (
        (!this.medicalPackages || this.medicalPackages.length == 0) &&
        (!this.surgeryHospitals || this.surgeryHospitals.length == 0) &&
        (!this.searchDoctors || this.searchDoctors.length == 0)
      );
    },
  },

  methods: {
    // ============ Main Search ============
    async doSearch() {
      if (this.isLoading || this.loadingDoctors || this.loadingSurgery) return;
      this.isLoading = true;
      this.searchError = false;
      this.hasSearched = true;
      this.pkgSearch.CountryID = this.search.CountryID || '';
      this.pkgSearch.MedicalPackageCategorID = this.search.MedicalPackageCategorID || '';
      this.pkgSearch.ValidDate = new Date();
      this.surgSearch.CountryID = this.search.CountryID || '';
      this.surgSearch.SurgeryCategoryID = this.search.SurgeryCategoryID || '';
      this.surgSearch.PageNumber = 1;
      this.docSearchModel.CountryID = this.search.CountryID || '';
      this.docSearchModel.SpecialtyID = this.search.SpecialtyID || '';
      this.docSearchModel.PageNumber = 1;
      this.$store.commit('MedicalPackageList/SET_MedicalPackage', []);
      this.$store.commit('SurgeryList/SET_SearchHospitals', []);
      this.$store.commit('DoctorList/SET_SearchDoctor', []);
      try {
        await Promise.all([
          this.$store.dispatch('MedicalPackageList/SearchMedicalPackage', { ...this.pkgSearch }).catch(() => { this.searchError = true; }),
          this.doSurgerySearch(), this.doDoctorSearch()
        ]);
      } finally { this.isLoading = false; }
    },

    // ============ Clear ============
    clearSearch() {
      if (this.isLoading || this.loadingDoctors || this.loadingSurgery) return;
      this.searchError = false;
      // 1) clear filter
      this.search = {
        CountryID: "",
        MedicalPackageCategorID: "",
        SurgeryCategoryID: "",
        SpecialtyID: "",
      };

      // 2) hide results
      this.hasSearched = false;

      // 3) clear store
      this.$store.commit('MedicalPackageList/SET_MedicalPackage', []);
      this.$store.commit("SurgeryList/SET_SearchHospitals", []);
      this.$store.commit("DoctorList/SET_SearchDoctor", []);

      // 4) reset paging
      this.surgSearch.PageNumber = 1;
      this.docSearchModel.PageNumber = 1;
      this.IsShowMoreSurgery = true;
      this.IsShowMoreDoctors = true;
    },

    // ==================== 1) Packages ====================
    OpenPackage(packageModel) {
      this.$router.push({ name: "PackageDetail", params: { PackageID: packageModel.ID } });
    },

    // ==================== 2) Surgery ====================
    async doSurgerySearch() {
      this.loadingSurgery = true;
      try {
        const res = await this.$store.dispatch('SurgeryList/SearchHospitalsPaging', { ...this.surgSearch });
        this.IsShowMoreSurgery = res.data.Data.length >= this.surgSearch.PageSize;
      } catch (error) {
        this.searchError = true;
        if (this.surgSearch.PageNumber > 1) this.surgSearch.PageNumber--;
      } finally { this.loadingSurgery = false; }
    },
    getNextHospitals() {
      if (this.loadingSurgery || this.isLoading) return;
      this.surgSearch.PageNumber++;
      this.doSurgerySearch();
    },
    getSurgeries(ID, HospitalID) {
      var obj = {
        SurgeyCategoryID: ID,
        HospitalID: HospitalID,
      };
      this.$store
        .dispatch("SurgeryList/GetAlHospitalSurgeyByCategory", obj)
        .then((res) => {
          if (res.data == undefined) this.ItemModel.Surgeries = res[0].data.Data;
          else this.ItemModel.Surgeries = res.data.Data;
          this.ItemModel.CatName = this.ItemModel.Surgeries[0].Surgery.SurgeryCategory.Name;
          this.showPopup = true;
        });
    },

    // ==================== 3) Doctors ====================
    async doDoctorSearch() {
      this.loadingDoctors = true;
      try {
        const res = await this.$store.dispatch('DoctorList/SearchDoctorsPaging', { ...this.docSearchModel });
        this.IsShowMoreDoctors = res.data.Data.length >= this.docSearchModel.PageSize;
      } catch (error) {
        this.searchError = true;
        if (this.docSearchModel.PageNumber > 1) this.docSearchModel.PageNumber--;
      } finally { this.loadingDoctors = false; }
    },
    getNextDoctors() {
      if (this.loadingDoctors || this.isLoading) return;
      this.docSearchModel.PageNumber++;
      this.doDoctorSearch();
    },
  },

  created() {
    // ===== Load Modules =====
    const modulesToRegister = [
      ["MedicalPackageList", moduleMedicalPackage],
      ["MedicalPackageCategoryList", moduleMedicalPackageCategory],
      ["CountryList", moduleCountry],
      ["SurgeryList", moduleSurgery],
      ["SurgeryCategoryList", moduleSurgeryCategory],
      ["DoctorList", moduleDoctor],
      ["SpecialtyList", moduleSpecialty],
    ];
    modulesToRegister.forEach(([name, mod]) => {
      if (!mod.isRegistered) {
        this.$store.registerModule(name, mod);
        mod.isRegistered = true;
      }
    });

    // ===== Load List Data =====
    this.$store.dispatch("CountryList/GetAllCountries");
    this.$store.dispatch("MedicalPackageCategoryList/SearchMedicalPackageCategories", {});
    this.$store.dispatch("SurgeryCategoryList/SearchSurgeryCategories", {});
    this.$store.dispatch("SpecialtyList/SearchSpecialties", {});
  },
};
</script>

<style scoped>
#MainSystemSearch { width: 100%; color: #132953; }
#MainSystemSearch .search-hint { font-size: 12px; font-weight: 400; line-height: 1.5; text-transform: none; }
#MainSystemSearch .care-search-button, #MainSystemSearch .care-clear { font-size: 14px; }
#MainSystemSearch .search-heading p { font-weight: 400; line-height: 1.5; }
#MainSystemSearch .search-heading h2 { font-weight: 600; letter-spacing: -.3px; }
#MainSystemSearch .search-field .vs__selected, #MainSystemSearch .search-field .vs__search { font-size: 13px; text-transform: none; }
#MainSystemSearch .care-search-button:hover:not(:disabled) { background: #106d3a; }
#MainSystemSearch .care-search-button { transition: background .2s; }
.care-search { position: relative; padding: 20px 24px 14px; background: white; border: 1px solid #e0e8ec; border-radius: 16px; box-shadow: 0 6px 20px rgba(19,41,83,.06); }
.search-heading { display: flex; justify-content: space-between; gap: 16px; align-items: center; margin-bottom: 18px; }
#MainSystemSearch .search-heading h2 { text-transform: none; font-size: 22px; margin: 0 0 6px; color: #132953; }
#MainSystemSearch .search-heading p, #MainSystemSearch .search-footnote { text-transform: none; color: #64748b; font-size: 13px; margin: 0; }
.search-heading > .feather-icon { color: #168348; }
.search-fields { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)) auto; gap: 14px; align-items: end; }
.search-field { min-width: 0; }
.search-field label { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; margin-bottom: 9px; }
.search-field label .feather-icon { width: 15px; height: 15px; color: #168348; }
.care-search-button { display: inline-flex; justify-content: center; align-items: center; gap: 9px; background: #168348; color: white; border: 0; border-radius: 10px; min-height: 46px; padding: 0 22px; font: inherit; font-weight: 600; cursor: pointer; }
.care-search-button .feather-icon { width: 18px; }
.search-footnote { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 22px; margin-top: 10px; }
.care-clear { border: 0; background: transparent; padding: 6px; color: #168348; font: inherit; font-weight: 600; cursor: pointer; }
.general-search-container { padding-top: 28px; }
.section-title { display: flex; align-items: center; gap: 12px; font-size: 22px; margin: 0 0 20px; text-align: start; }
.section-title::before { content: ''; width: 4px; height: 24px; border-radius: 4px; background: #168348; }
.general-search-container section { background: white; padding: 24px; border: 1px solid #e3eaf0; border-radius: 18px; margin-bottom: 24px; }
.care-package-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; }
.care-package { padding: 0; overflow: hidden; border: 1px solid #e3eaf0; background: #f3f7f8; border-radius: 14px; text-align: start; cursor: pointer; font: inherit; color: #132953; transition: box-shadow .2s; }
.care-package:hover { box-shadow: 0 8px 24px rgba(19,41,83,.12); }
.care-package > img { width: 100%; height: 190px; object-fit: cover; display: block; }
.care-package-body { padding: 18px; background: white; }
.care-package-category { color: #64748b; font-size: 12px; }
.care-package h4 { margin: 8px 0 18px; font-size: 16px; line-height: 1.5; overflow-wrap: anywhere; }
.care-package-link { display: flex; justify-content: space-between; color: #168348; font-weight: 600; font-size: 13px; }
.care-more { display: block; margin: 24px auto 0; padding: 12px 26px; border: 1px solid #cbdcd2; border-radius: 10px; color: #168348; background: #f1faf5; font: inherit; cursor: pointer; }
.search-state { padding: 36px 20px; text-align: center; color: #64748b; }
.search-error { padding: 18px; margin-top: 20px; background: #fff5ed; color: #8a4714; border: 1px solid #f0d9bd; border-radius: 12px; }
.care-spinner { display: inline-block; width: 20px; height: 20px; border: 2px solid #dcebe2; border-top-color: #168348; border-radius: 50%; margin-inline-end: 12px; vertical-align: middle; animation: care-spin .8s linear infinite; }
@keyframes care-spin { to { transform: rotate(360deg); } }
button:disabled { opacity: .6; cursor: wait; }
button:focus-visible { outline: 3px solid #168348; outline-offset: 3px; }
@media (max-width: 1050px) { .search-fields { grid-template-columns: repeat(2, minmax(0, 1fr)); } .care-search-button { grid-column: 1 / -1; } .care-package-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 600px) { .care-search { padding: 20px 16px; } .search-fields, .care-package-grid { grid-template-columns: 1fr; } .search-heading h2 { font-size: 20px; } .general-search-container section { padding: 18px 12px; } .search-footnote { align-items: flex-start; } }
@media (prefers-reduced-motion: reduce) { .care-spinner { animation: none; } }
</style>
<style>
#MainSystemSearch .search-field .vs__dropdown-toggle { min-height: 46px; border: 1px solid #dce4eb; border-radius: 10px; background: #f8fafc; padding: 4px 6px; }
#MainSystemSearch .search-field .vs--open .vs__dropdown-toggle { border-color: #168348; }
#MainSystemSearch .search-field .vs__dropdown-menu { z-index: 50; min-width: 100%; border: 1px solid #dce4eb; border-radius: 10px; box-shadow: 0 10px 24px rgba(19,41,83,.12); }
#MainSystemSearch .search-field .vs__selected { max-width: 100%; white-space: normal; overflow-wrap: anywhere; color: #132953; font-size: 13px; }
#MainSystemSearch .search-field .vs__search { width: 0; font-size: 13px; }
#MainSystemSearch .search-field .vs__dropdown-option--highlight { background: #168348; }
</style>
